import { buildBasicFieldLayoutRows } from '@/utils/printBasicFieldsLayout'
import {
  mergeTableConfigFromRaw,
  getNormalizedTableColumnWidthPercents,
  shouldBlankMoneyCellForPrint,
  getTableColumnsForPrint,
  getMergedTableHeaderCells,
  mergedTableHeaderPresentationStyle,
  effectiveTableHeaderTextAlign,
  isTableRowHeightLocked
} from '@/utils/printTableLayout'
import {
  getTotalRowCellText,
  shouldShowTotalsRow,
  PRINT_PREVIEW_LEGACY_COLUMNS
} from '@/utils/printTableTotals'
import { migrateSignatureItems, displayLabelForItem, resolveSignatureLineValue } from '@/utils/printSignatures'
import { formatDeliveryOrderPrintQuantity } from '@/utils/formatDeliveryOrderPrintQuantity'
import {
  shouldRenderHeaderLines,
  shouldRenderFooterLines,
  headerLinesPlainProbe,
  footerLinesPlainProbe
} from '@/utils/printHeaderFooterLines'

export const PRINT_TEMPLATE_TYPE = 'delivery_order'

/** 导出用：保证工作表上出现「送货单号」文案（模板未含占位时也能打印对账） */
export function deliveryOrderNoBannerText(printData) {
  const raw = printData?.delivery_order_no
  const no = raw != null && String(raw).trim() ? String(raw).trim() : ''
  return `送货单号：${no || '—'}`
}

export function bannerAlreadyInExportText(text, printData) {
  const no = printData?.delivery_order_no
  if (no != null && String(no).trim() && text.includes(String(no).trim())) return true
  if (/送货单号\s*[:：]/.test(text)) return true
  return false
}

/** 拼接页眉与各基础字段字面量，用于判断是否已包含「送货单号」避免导出重复一行 */
export function buildDeliveryNoBannerProbeText(templateConfig, printData) {
  let basicBannerProbe = ''
  if (shouldRenderHeaderLines(templateConfig)) {
    basicBannerProbe = headerLinesPlainProbe(
      templateConfig,
      (s) => replaceDeliveryVariables(String(s), printData),
      richHtmlToPlainText
    )
  } else {
    const headerHtml = replaceDeliveryVariables(templateConfig.header || '', printData)
    basicBannerProbe = richHtmlToPlainText(headerHtml)
  }
  const basicRowsPre = buildBasicFieldLayoutRows(templateConfig.basicFields || [])
  for (const brow of basicRowsPre) {
    if (brow.type === 'split') {
      const splitFields = [...(brow.left || []), ...(brow.right || [])]
      splitFields.forEach((f) => {
        basicBannerProbe += ` ${getDeliveryFieldValue(f.field, f, printData)}`
      })
    } else if (brow.type === 'grid') {
      const gridFields = brow.fields || []
      gridFields.forEach((f) => {
        basicBannerProbe += ` ${getDeliveryFieldValue(f.field, f, printData)}`
      })
    } else {
      const lineFields = brow.fields || []
      lineFields.forEach((f) => {
        basicBannerProbe += ` ${getDeliveryFieldValue(f.field, f, printData)}`
      })
    }
  }
  return basicBannerProbe
}

const DEFAULT_PAGE = {
  paperSize: 'A4',
  orientation: 'portrait',
  marginTop: 10,
  marginRight: 10,
  marginBottom: 10,
  marginLeft: 10,
  headerRootFontSizePx: 11
}

function mmToInch(mm) {
  return (Number(mm) || 0) / 25.4
}

export function mergeTemplatePage(rawPage) {
  return { ...DEFAULT_PAGE, ...(rawPage || {}) }
}

function excelPaperAndMargins(page) {
  const p = mergeTemplatePage(page)
  const paperSizeMap = { A4: 9, A3: 8, Letter: 1 }
  const paperSize = paperSizeMap[p.paperSize] || 9
  const orientation = p.orientation === 'landscape' ? 'landscape' : 'portrait'
  const margins = {
    left: mmToInch(p.marginLeft ?? 10),
    right: mmToInch(p.marginRight ?? 10),
    top: mmToInch(p.marginTop ?? 10),
    bottom: mmToInch(p.marginBottom ?? 10),
    header: 0.2,
    footer: 0.35
  }
  return { paperSize, orientation, margins }
}

function templateFooterPlain(html) {
  if (!html || typeof html !== 'string') return ''
  if (typeof document !== 'undefined') {
    const d = document.createElement('div')
    d.innerHTML = html
    return (d.textContent || d.innerText || '').replace(/\s+/g, ' ').trim()
  }
  return String(html).replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim()
}

function normalizeExcelFooterText(text) {
  if (!text) return ''
  let t = String(text)
  t = t.replace(/\{page_number\}/gi, '&P')
  t = t.replace(/\{total_pages\}/gi, '&N')
  return t.trim()
}

function richHtmlToPlainText(html) {
  if (!html) return ''
  let s = String(html)
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n')
    .replace(/<\/div>/gi, '\n')
    .replace(/<\/h[1-6]>/gi, '\n')
  if (typeof document !== 'undefined') {
    const d = document.createElement('div')
    d.innerHTML = s
    return (d.textContent || '')
      .split('\n')
      .map((l) => l.replace(/\s+/g, ' ').trim())
      .filter(Boolean)
      .join('\n')
      .trim()
  }
  return s.replace(/<[^>]+>/g, '\n').replace(/\n+/g, '\n').trim()
}

function getSingleItemContext(printData) {
  const arr = printData?.items
  if (!Array.isArray(arr) || arr.length !== 1) return null
  return arr[0]
}

export function replaceDeliveryVariables(text, printData) {
  if (!text || !printData) return text || ''
  let result = String(text)
  Object.keys(printData).forEach((key) => {
    if (typeof printData[key] === 'string' || typeof printData[key] === 'number') {
      result = result.replace(new RegExp(`\\{${key}\\}`, 'g'), printData[key])
    }
  })
  const one = getSingleItemContext(printData)
  if (one) {
    Object.keys(one).forEach((key) => {
      if (typeof one[key] === 'string' || typeof one[key] === 'number') {
        result = result.replace(new RegExp(`\\{${key}\\}`, 'g'), one[key])
      }
    })
  }
  result = result.replace(/\{current_date\}/g, new Date().toLocaleDateString('zh-CN'))
  result = result.replace(/\{current_time\}/g, new Date().toLocaleTimeString('zh-CN'))
  return result
}

export function getDeliveryFieldValue(field, fieldDef, printData) {
  if (!printData) return ''
  if (!field || String(field).trim() === '') return ''
  if (field === '__custom_text') return String(fieldDef?.customValue || '')
  const one = getSingleItemContext(printData)
  let value = printData[field]
  if ((value === null || value === undefined || value === '') && field !== 'items') {
    if (one && Object.prototype.hasOwnProperty.call(one, field)) {
      value = one[field]
    }
  }
  if (field === 'quantity' || field === 'delivery_quantity') {
    const v =
      value ??
      (field === 'delivery_quantity' ? one?.delivery_quantity : null) ??
      one?.quantity ??
      printData.delivery_quantity ??
      printData.quantity
    return formatDeliveryOrderPrintQuantity(v ?? '')
  }
  if (value === null || value === undefined) return ''
  return String(value)
}

export function getDeliveryTableCellValue(item, col, rowIndex, tableConfig) {
  const field = col.field
  if (field == null || String(field).trim() === '') return ''
  if (shouldBlankMoneyCellForPrint(field, tableConfig)) return ''

  if (field === 'product_name') {
    const v =
      item.product_name != null && String(item.product_name).trim()
        ? String(item.product_name).trim()
        : ''
    return v || '-'
  }
  if (field === 'material_summary') {
    const ms = item.material_summary != null ? String(item.material_summary).trim() : ''
    if (ms) return ms
    const n = (item.product_name != null ? String(item.product_name).trim() : '') || ''
    const ce = (item.color_en != null ? String(item.color_en).trim() : '') || ''
    const cc = (item.color_cn != null ? String(item.color_cn).trim() : '') || ''
    const colorPart =
      ce && cc && ce !== cc
        ? `${ce} / ${cc}`
        : ce || cc || (item.color != null ? String(item.color).trim() : '') || ''
    if (n && colorPart) return `${n} / ${colorPart}`
    return n || colorPart || '-'
  }

  if (col.field === 'serial') {
    return item.serial || rowIndex + 1
  }
  if (col.field === 'color') {
    if (item.color_en && item.color_cn) return `${item.color_en} / ${item.color_cn}`
    if (item.color_en) return item.color_en
    if (item.color_cn) return item.color_cn
    return item.color || '-'
  }
  if (col.field === 'quantity') {
    const q = item[col.field] ?? item.delivery_quantity
    return formatDeliveryOrderPrintQuantity(q ?? '')
  }
  if (col.field === 'unit_price' || col.field === 'line_amount' || col.field === 'amount' || col.field === 'item_amount') {
    const v = (() => {
      const f = col.field
      if (f === 'unit_price') return item.unit_price ?? item.unitPrice
      if (f === 'line_amount') return item.line_amount ?? item.item_amount ?? item.amount
      if (f === 'item_amount') return item.item_amount ?? item.line_amount ?? item.amount
      if (f === 'amount') return item.amount ?? item.line_amount ?? item.item_amount
      return item[f]
    })()
    if (v === undefined || v === null || v === '') return '-'
    return Number(v).toFixed(2)
  }
  if (col.field === 'dye_fee') {
    const v = item.dye_fee ?? item.dyeFee ?? 0
    if (v === undefined || v === null || v === '') return '-'
    return Number(v).toFixed(2)
  }

  const raw = item[col.field]
  if (raw === undefined || raw === null || raw === '') return '-'
  return raw
}

export function resolvePaymentFooterField(pf, fieldKey) {
  if (!pf) return ''
  if (pf[fieldKey]) return String(pf[fieldKey])
  const selected = (pf.accounts || []).find((x) => x.key === pf.selectedAccountKey)
  return selected ? String(selected[fieldKey] || '') : ''
}

export function resolveColumnFontSizePx(col, deliveryDefaultPx) {
  const n = Number(col?.fontSize)
  if (Number.isFinite(n) && n > 0) return n
  return deliveryDefaultPx
}

function excelHorizontalAlign(col, isHeader) {
  const a = isHeader ? effectiveTableHeaderTextAlign(col) : col?.align || 'left'
  if (a === 'right') return 'right'
  if (a === 'center') return 'center'
  return 'left'
}

export function signatureExcelValue(item, printData) {
  const fixed = String(item?.fixedName ?? item?.fixed_name ?? '').trim()
  if (fixed) return fixed
  return resolveSignatureLineValue(item, (k) => getDeliveryFieldValue(k, null, printData) || '')
}

/**
 * 拉取出库单打印上下文（模板 + 打印数据），与「预览打印」同源。
 */
export async function loadDeliveryOrderPrintContext(deliveryOrderId) {
  const id = Number(deliveryOrderId)
  if (!Number.isFinite(id) || id <= 0) {
    throw new Error('无效的出库单 ID')
  }

  const { getPrintData } = await import('@/api/printTemplates')
  const res = await getPrintData(PRINT_TEMPLATE_TYPE, id, {})

  if (!res?.success) {
    throw new Error(res?.message || '获取打印数据失败')
  }
  const tpl = res.data?.template
  const printData = res.data?.print_data
  if (!tpl || !printData) {
    throw new Error(res?.message || '打印模板或数据不完整')
  }

  const raw = tpl.template_config || {}
  const rawTc = raw.tableConfig || {}
  const templateConfig = {
    fields: [],
    header: '',
    footer: '',
    footerAlign: 'left',
    ...raw,
    page: mergeTemplatePage(raw.page || {}),
    tableConfig: mergeTableConfigFromRaw(rawTc)
  }

  return { templateConfig, printData, tpl, id }
}

/**
 * 导出出库单 Excel：模板与数据源与「出库单打印预览」一致（默认模板由后端解析）。
 */
export async function exportDeliveryOrderPrintExcel(deliveryOrderId) {
  const { templateConfig, printData, id } = await loadDeliveryOrderPrintContext(deliveryOrderId)
  const tc = templateConfig.tableConfig || {}
  const cols =
    Array.isArray(tc.columns) && tc.columns.length
      ? getTableColumnsForPrint(tc.columns)
      : PRINT_PREVIEW_LEGACY_COLUMNS

  const colCount = Math.max(1, cols.length)
  const pcts = getNormalizedTableColumnWidthPercents(cols)
  const deliveryBodyFs = 12

  const { default: ExcelJS } = await import('exceljs')
  const wb = new ExcelJS.Workbook()
  wb.creator = 'ERP'

  const { paperSize, orientation, margins } = excelPaperAndMargins(templateConfig.page)

  const ws = wb.addWorksheet('送货单', {
    pageSetup: {
      paperSize,
      orientation,
      margins,
      // 横向缩至 1 页宽，避免列被裁切；fitToHeight 用大值表示纵向多页（0 在部分环境下会被忽略）
      fitToWidth: 1,
      fitToHeight: 999,
      fitToPage: true,
      horizontalCentered: true,
      verticalCentered: false
    },
    views: [{ showGridLines: false }]
  })

  if (ws.properties) {
    ws.properties.outlineLevelCol = 0
    ws.properties.outlineLevelRow = 0
  }

  // 略加大列宽预算，减少「列宽不够、打印需手调」；配合 fitToWidth 仍可在页宽内缩放
  const totalCharBudget = orientation === 'landscape' ? 132 : 94
  for (let i = 1; i <= colCount; i++) {
    const pct = pcts[i - 1] != null ? pcts[i - 1] : 100 / colCount
    ws.getColumn(i).width = Math.max(6.2, Math.round((pct / 100) * totalCharBudget))
  }

  const thin = { style: 'thin', color: { argb: 'FF000000' } }
  const boxBorder = { top: thin, left: thin, bottom: thin, right: thin }

  let r = 1

  const mergeRow = (fromR, fromC, toR, toC) => {
    if (fromC > toC || fromR > toR) return
    ws.mergeCells(fromR, fromC, toR, toC)
  }

  const pageMerged = mergeTemplatePage(templateConfig.page)

  if (shouldRenderHeaderLines(templateConfig)) {
    const lines = templateConfig.headerLines || []
    const plainLines = []
    let maxFs = Math.max(9, Math.min(22, Number(pageMerged.headerRootFontSizePx) || 11))
    let estHeight = 0
    const lhDefault =
      Number(pageMerged.headerContentLineHeight) > 0 ? Number(pageMerged.headerContentLineHeight) : 1.22
    const defaultFsBase = Math.min(22, Math.max(7, Number(pageMerged.headerRootFontSizePx) || 11))
    for (const line of lines) {
      const raw = replaceDeliveryVariables(String(line?.content || ''), printData)
      const plain = richHtmlToPlainText(raw).trim()
      if (!plain) continue
      plainLines.push(plain)
      const fsRaw = Number(line?.fontSizePx)
      const fs =
        Number.isFinite(fsRaw) && fsRaw > 0
          ? Math.min(22, Math.max(7, Math.round(fsRaw)))
          : defaultFsBase
      maxFs = Math.max(maxFs, fs)
      const lhLineRaw = Number(line?.lineHeight)
      const lhLine =
        Number.isFinite(lhLineRaw) && lhLineRaw > 0 ? lhLineRaw : lhDefault
      const minHPx = Math.max(
        Number(pageMerged.headerLineMinHeightPx) || 0,
        Number(line?.minHeightPx) || 0
      )
      const fixedHPx = Number(line?.heightPx)
      const wrapLines = Math.max(1, plain.split('\n').length)
      const lineBox = Number.isFinite(fixedHPx) && fixedHPx > 0
        ? fixedHPx
        : Math.max(minHPx || 0, fs * lhLine * wrapLines * 1.12)
      estHeight += Math.ceil(lineBox)
    }
    const merged = plainLines.join('\n')
    if (merged) {
      ws.getCell(r, 1).value = merged
      ws.getCell(r, 1).alignment = { horizontal: 'center', vertical: 'top', wrapText: true }
      ws.getCell(r, 1).font = { size: maxFs }
      mergeRow(r, 1, r, colCount)
      const fallbackH = merged.split('\n').length * (maxFs * 1.35)
      ws.getRow(r).height = Math.min(240, Math.max(28, estHeight || fallbackH))
      r++
      r++
    }
  } else {
    const headerHtml = replaceDeliveryVariables(templateConfig.header || '', printData)
    const headerPlain = richHtmlToPlainText(headerHtml)
    if (headerPlain) {
      const fs = Math.max(9, Math.min(22, Number(templateConfig.page.headerRootFontSizePx) || 11))
      ws.getCell(r, 1).value = headerPlain
      ws.getCell(r, 1).alignment = { horizontal: 'center', vertical: 'top', wrapText: true }
      ws.getCell(r, 1).font = { size: fs }
      mergeRow(r, 1, r, colCount)
      ws.getRow(r).height = Math.min(240, Math.max(28, headerPlain.split('\n').length * (fs * 1.35)))
      r++
      r++
    }
  }

  const noBanner = deliveryOrderNoBannerText(printData)
  const basicBannerProbe = buildDeliveryNoBannerProbeText(templateConfig, printData)
  if (!bannerAlreadyInExportText(basicBannerProbe, printData)) {
    ws.getCell(r, 1).value = noBanner
    ws.getCell(r, 1).font = { bold: true, size: Math.max(deliveryBodyFs, 12) }
    ws.getCell(r, 1).alignment = { horizontal: 'right', vertical: 'middle', wrapText: true }
    mergeRow(r, 1, r, colCount)
    ws.getRow(r).height = 22
    r++
    r++
  }

  const basicRows = buildBasicFieldLayoutRows(templateConfig.basicFields || [])
  for (const brow of basicRows) {
    if (brow.type === 'split') {
      const leftTxt = brow.left
        .map((f) => {
          const lbl = f.label ? `${f.label}: ` : ''
          return `${lbl}${getDeliveryFieldValue(f.field, f, printData)}${f.suffix || ''}`
        })
        .join('    ')
      const rightTxt = brow.right
        .map((f) => {
          const lbl = f.label ? `${f.label}: ` : ''
          return `${lbl}${getDeliveryFieldValue(f.field, f, printData)}${f.suffix || ''}`
        })
        .join('\n')
      const mid = Math.max(1, Math.floor(colCount / 2))
      ws.getCell(r, 1).value = leftTxt
      ws.getCell(r, 1).alignment = { horizontal: 'left', vertical: 'top', wrapText: true }
      ws.getCell(r, 1).font = { size: deliveryBodyFs }
      mergeRow(r, 1, r, mid)

      ws.getCell(r, mid + 1).value = rightTxt
      ws.getCell(r, mid + 1).alignment = { horizontal: 'right', vertical: 'top', wrapText: true }
      ws.getCell(r, mid + 1).font = { size: deliveryBodyFs }
      mergeRow(r, mid + 1, r, colCount)
      ws.getRow(r).height = 22
      r++
    } else if (brow.type === 'grid') {
      const fields = brow.fields || []
      const sumSpan = fields.reduce((s, f) => s + Math.max(1, Math.min(12, Number(f.__colSpan ?? f.colSpan ?? 12))), 0) || 12
      let c0 = 1
      for (const f of fields) {
        const span = Math.max(1, Math.min(12, Number(f.__colSpan ?? f.colSpan ?? 12)))
        const wcols = Math.max(1, Math.round((span / sumSpan) * colCount))
        const c1 = Math.min(colCount, c0 + wcols - 1)
        const lbl = f.label ? `${f.label}: ` : ''
        ws.getCell(r, c0).value = `${lbl}${getDeliveryFieldValue(f.field, f, printData)}${f.suffix || ''}`
        ws.getCell(r, c0).alignment = { horizontal: 'left', vertical: 'top', wrapText: true }
        ws.getCell(r, c0).font = { size: resolveColumnFontSizePx(f, deliveryBodyFs) }
        if (c1 > c0) mergeRow(r, c0, r, c1)
        c0 = c1 + 1
        if (c0 > colCount) break
      }
      ws.getRow(r).height = 20
      r++
    } else {
      const line = (brow.fields || [])
        .map((f) => {
          const lbl = f.label ? `${f.label}: ` : ''
          return `${lbl}${getDeliveryFieldValue(f.field, f, printData)}${f.suffix || ''}`
        })
        .join('    ')
      ws.getCell(r, 1).value = line
      ws.getCell(r, 1).font = { size: deliveryBodyFs }
      ws.getCell(r, 1).alignment = { horizontal: 'left', vertical: 'top', wrapText: true }
      mergeRow(r, 1, r, colCount)
      ws.getRow(r).height = 20
      r++
    }
  }

  if (basicRows.length) {
    r++
  }

  const ttm = Math.max(0, Number(templateConfig.page?.tableSectionMarginTopPx ?? 0) || 0)
  if (ttm > 0) {
    ws.getRow(r).height = Math.min(ttm / 1.5, 40)
    r++
  }

  const headerTableRow = r
  getMergedTableHeaderCells(cols).forEach((cell) => {
    const fromC = cell.startIndex + 1
    const toC = fromC + cell.colspan - 1
    const col = cell.col
    const cellRef = ws.getCell(r, fromC)
    cellRef.value = cell.label || ''
    cellRef.font = { bold: true, size: resolveColumnFontSizePx(col, deliveryBodyFs) }
    const mergedStyle = mergedTableHeaderPresentationStyle(cell.colspan)
    cellRef.alignment = {
      horizontal: mergedStyle ? 'center' : excelHorizontalAlign(col, true),
      vertical: mergedStyle ? 'middle' : 'middle',
      wrapText: true
    }
    cellRef.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFFFF' } }
    cellRef.border = boxBorder
    if (cell.colspan > 1) mergeRow(r, fromC, r, toC)
  })
  const rowLocked = isTableRowHeightLocked(tc)
  const rowH = rowLocked && Number(tc.rowHeight) > 0 ? Number(tc.rowHeight) * 0.75 : null
  ws.getRow(r).height = rowH != null ? rowH : 20
  r++

  const items = Array.isArray(printData.items) ? printData.items : []
  items.forEach((item, idx) => {
    cols.forEach((col, cidx) => {
      const cell = ws.getCell(r, cidx + 1)
      const text = getDeliveryTableCellValue(item, col, idx, tc)
      cell.value = text === null || text === undefined ? '' : text
      cell.font = { size: resolveColumnFontSizePx(col, deliveryBodyFs) }
      cell.alignment = {
        horizontal: excelHorizontalAlign(col, false),
        vertical: 'top',
        wrapText: true
      }
      cell.border = boxBorder
    })
    ws.getRow(r).height = rowH != null ? rowH : 21
    r++
  })

  const totalsCfg = { ...tc, columns: cols }
  if (shouldShowTotalsRow(printData, totalsCfg)) {
    cols.forEach((col, cidx) => {
      const cell = ws.getCell(r, cidx + 1)
      cell.value = getTotalRowCellText(col, printData, cols, cidx, totalsCfg, PRINT_TEMPLATE_TYPE)
      cell.font = { bold: true, size: resolveColumnFontSizePx(col, deliveryBodyFs) }
      cell.alignment = {
        horizontal: excelHorizontalAlign(col, false),
        vertical: 'middle',
        wrapText: true
      }
      cell.border = boxBorder
    })
    ws.getRow(r).height = rowH != null ? rowH : 20
    r++
  }

  r++

  const notesRaw =
    templateConfig.notes && String(templateConfig.notes.content || '').trim()
      ? String(templateConfig.notes.content).trim()
      : ''
  const notesText = notesRaw ? replaceDeliveryVariables(notesRaw, printData) : ''
  const notesMt = Math.max(0, Number(templateConfig.page?.notesMarginTopPx ?? 6) || 0)
  if (notesMt > 8) {
    ws.getRow(r).height = Math.min(notesMt / 2, 36)
    r++
  }
  if (notesText) {
    ws.getCell(r, 1).value = notesText
    ws.getCell(r, 1).font = { size: Math.max(10, deliveryBodyFs - 1) }
    ws.getCell(r, 1).alignment = { horizontal: 'left', vertical: 'top', wrapText: true }
    mergeRow(r, 1, r, colCount)
    ws.getRow(r).height = Math.min(180, Math.max(24, notesText.split('\n').length * 14))
    r++
    r++
  }

  const pf = templateConfig.paymentFooter
  const payMt = Math.max(0, Number(templateConfig.page?.paymentFooterMarginTopPx ?? 8) || 0)
  if (payMt > 8 && pf?.enabled) {
    ws.getRow(r).height = Math.min(payMt / 2, 36)
    r++
  }
  if (pf && pf.enabled !== false) {
    ws.getCell(r, 1).value = pf.noticeText || '请将以上货款汇入以下银行账户'
    ws.getCell(r, 1).font = { bold: true, size: deliveryBodyFs }
    mergeRow(r, 1, r, colCount)
    r++
    ws.getCell(r, 1).value = `户名：${resolvePaymentFooterField(pf, 'accountName') || '—'}`
    ws.getCell(r, 1).font = { size: deliveryBodyFs }
    mergeRow(r, 1, r, colCount)
    r++
    ws.getCell(r, 1).value = `开户行：${resolvePaymentFooterField(pf, 'bankName') || '—'}`
    ws.getCell(r, 1).font = { size: deliveryBodyFs }
    mergeRow(r, 1, r, colCount)
    r++
    ws.getCell(r, 1).value = `账号：${resolvePaymentFooterField(pf, 'accountNo') || '—'}`
    ws.getCell(r, 1).font = { size: deliveryBodyFs }
    mergeRow(r, 1, r, colCount)
    r++
    r++
  }

  const signsMt = Math.max(0, Number(templateConfig.page?.signaturesMarginTopPx ?? 10) || 0)
  if (signsMt > 8) {
    ws.getRow(r).height = Math.min(signsMt / 2, 40)
    r++
  }

  const sigItems = migrateSignatureItems(templateConfig.signatures)
  if (sigItems.length > 0) {
    const sigLabelRow = r
    const sigValueRow = r + 1
    const n = sigItems.length
    for (let i = 0; i < n; i++) {
      const fromC = Math.floor((i * colCount) / n) + 1
      const toC = Math.floor(((i + 1) * colCount) / n)
      const item = sigItems[i]
      const label = displayLabelForItem(item)
      const val = signatureExcelValue(item, printData)

      const labelCell = ws.getCell(sigLabelRow, fromC)
      labelCell.value = `${label}：`
      labelCell.alignment = { horizontal: 'center', vertical: 'bottom', wrapText: true }
      labelCell.font = { size: deliveryBodyFs }

      const valueCell = ws.getCell(sigValueRow, fromC)
      valueCell.value = val
      valueCell.alignment = { horizontal: 'center', vertical: 'top', wrapText: true }
      valueCell.font = { size: deliveryBodyFs }

      if (toC > fromC) {
        mergeRow(sigLabelRow, fromC, sigLabelRow, toC)
        mergeRow(sigValueRow, fromC, sigValueRow, toC)
      }
    }
    ws.getRow(sigLabelRow).height = 22
    ws.getRow(sigValueRow).height = 26
    r += 2
  }

  const pageText = '第 &P 页，共 &N 页'
  let footerText = ''
  if (shouldRenderFooterLines(templateConfig)) {
    footerText = normalizeExcelFooterText(
      footerLinesPlainProbe(
        templateConfig,
        (s) => replaceDeliveryVariables(String(s), printData),
        templateFooterPlain
      )
    )
  } else {
    footerText = normalizeExcelFooterText(templateFooterPlain(templateConfig.footer))
  }
  const hasPageVar = /&P|&N/i.test(footerText)
  ws.headerFooter.oddFooter = footerText ? (hasPageVar ? footerText : `${footerText}    ${pageText}`) : pageText

  if (items.length > 0 && headerTableRow > 0) {
    ws.pageSetup.printTitlesRow = `${headerTableRow}:${headerTableRow}`
  }

  const fileBase = printData.delivery_order_no || printData.order_no || `delivery_${id}`
  const fileName = `${fileBase}_送货单.xlsx`
  const buf = await wb.xlsx.writeBuffer()
  const blob = new Blob([buf], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = fileName
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}
